import { useMemo } from 'react';
import { DateTime as DT } from 'luxon';
import { UserNameItem } from 'modules/user';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import EOverlay from 'components/others/EOverlay';
import { escapeHtml } from 'utils/helpers/functions/escapeHtml';

const formatPaymentAmount = (amount: number, isUsd: boolean) => {
    if (isUsd) return format$Price(amount);
    return amount >= 0
        ? withLocalCurrency(formatPrice(amount))
        : withLocalCurrency(formatPrice(-amount), true);
};

interface Props {
    payment: Record<string, any>;
}

interface AttachLink {
    href: string;
    label: string;
}

const fileLabelFromUrl = (fileName: string) => {
    try {
        const url = new URL(fileName);
        return decodeURIComponent(url.pathname.split('/').pop() || fileName);
    } catch {
        return fileName.split('/').pop() || fileName;
    }
};

const PaymentInfo = ({ payment }: Props) => {
    const isUsd = Boolean(Number(payment.paymentUsd ?? payment.payment_usd ?? 0));
    const attachments = useMemo(
        () =>
            String(payment.paymentAttach ?? payment.payment_attach ?? '')
                .split(';')
                .map((item) => item.trim())
                .filter(Boolean)
                .map((href) => ({ href, label: fileLabelFromUrl(href) })),
        [payment.paymentAttach, payment.payment_attach],
    );
    const description = String(payment.paymentDescription ?? payment.payment_description ?? '').trim();

    return (
        <tr>
            <td className="date">
                <span className="d-block">{DT.fromMillis(payment.dt).toLocaleString(DT.DATE_MED)}</span>
                <span className="text-muted fs-12">{DT.fromMillis(payment.dt).toLocaleString(DT.TIME_SIMPLE)}</span>
            </td>
            <td className="user">
                <UserNameItem
                    user={{
                        userName: payment.userName,
                        userEmail: payment.userEmail,
                        userPicture: payment.userPicture,
                    }}
                />
            </td>
            <td className={`amount ${payment.payment > 0 ? 'text-success' : 'text-danger'}`}>
                <span className="fw-bold">{formatPaymentAmount(Number(payment.payment || 0), isUsd)}</span>
            </td>
            <td className="description">
                {description ? (
                    <EOverlay text={`<div>${escapeHtml(description)}</div>`} />
                ) : (
                    <span className="text-muted">-</span>
                )}
            </td>
            <td className="attach">
                {attachments.length > 0 ? (
                    <div className="off-payments-attach-list">
                        {attachments.map((file: AttachLink) => (
                            <a
                                key={`${file.href}-${file.label}`}
                                href={file.href}
                                target="_blank"
                                rel="noreferrer"
                                className="off-payments-attach-link text-truncate"
                                title={file.label}
                            >
                                <i className="bi bi-paperclip me-1"></i>
                                {file.label}
                            </a>
                        ))}
                    </div>
                ) : (
                    <span className="text-muted">-</span>
                )}
            </td>
        </tr>
    );
};

export default PaymentInfo;
